import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserCreateModel } from '../../settings/interfaces/user';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LimegearUserModel } from '../classes/limegearUser.model';
import { ReusableFunctions } from '../classes/reusable-functions';
import { UserAccessService } from './user-access.service';
import { UserPasswordResetRequest } from '../classes/user-password-reset';

export interface User {
    clientCustomRoleName: string;
    loginEmail: string;
    password: string;
    pwHash: string;
    failedLoginAttempt: number;
    lockoutEndDate: Date;
    isSuperUser: boolean;
    timeZoneKey: number;
    isDSTObserved: boolean;
    lastLoginDate: Date;
    activeDate: Date;
    inActiveDate: Date;
    userGUID: string;
    isActive: boolean;
    isDisabled: boolean;
    isDeleted: boolean;
    lastMod: Date;
    lastModUserKey: number;
    isClientAdmin: boolean;
    permissionSetName: string;
    customPermissionSetKey?: number;
    positionName: string;
    hasDefaultPermissions: boolean;
    hasDefaultAccess: boolean;
    isClientOwner: boolean;
    hasGLAccess: boolean;
    alternateLoginEmail: string;
}

export interface UpdateUser {
    clientCustomRoleName: string;
    inActiveDate: Date;
    isActive: boolean;
    isDisabled: boolean;
    lastMod: Date;
    lastModUserKey: number;
    loginEmail: string;
    isClientAdmin: boolean;
    hasDefaultAccess: boolean;
    hasDefaultPermissions: boolean;
    customPermissionSetKey?: number;
    permissionSetName: string;
    positionName: string;
    hasGLAccess?: boolean;
    isClientOwner?: boolean;
    alternateLoginEmail?: string;
    hasAlternateLoginEmail?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private serverUrl = 'https://' + window.location.host;
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/user').replace('{version}', this.version);

    constructor(private http: HttpClient) {}

    currentUser: LimegearUserModel;

    getUser(id: number): Observable<User> {
        return this.http.get<User>(this._url + '/' + id);
    }

    getUserByEmail(email: string): Observable<User> {
        return this.http.post<User>(this._url + '/GetByEmail/', {
            Email: email,
        });
    }

    getUserByUsername(username: string): Observable<User> {
        return this.http.post<User>(this._url + '/GetByUsername/', {
            Email: username,
        });
    }

    editUser(user: UpdateUser) {
        return new Observable((subscriber) => {
            this.http.patch<number>(this._url, user).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    createUser(user: UserCreateModel): Observable<any> {
        return new Observable((subscriber) => {
            this.http.post<any>(this._url + '/createNewUser', user).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.next(false);
                }
            );
        });
    }

    createBulkUsers(users: UserCreateModel[]): Observable<any> {
        return this.http.post<any>(this._url + '/create/bulk', users);
    }

    sendWelcomeEmail(users: UserPasswordResetRequest[]): Observable<any> {
        return this.http.post<UserPasswordResetRequest>(this._url + '/WelcomeEmail', users);
    }

    isLoginEmailAvailable(request: any): Observable<boolean> {
        return this.http.post<boolean>(this._url + '/IsLoginEmailAvailable', request);
    }
}
